import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  FaHeartbeat,
  FaThermometerHalf,
  FaTint,
  FaWind,
  FaNotesMedical,
  FaPlusSquare,
} from "react-icons/fa";
import { GiHealthNormal } from "react-icons/gi"; // Example for O2 Sat

// Helper to render value, supporting both old and new data structures
const getVitalValue = (entry, vitalConfig) => {
  // New structure is prioritized
  if (entry.vitals && entry.vitals[vitalConfig.id] !== undefined) {
    const value = entry.vitals[vitalConfig.id];
    if (vitalConfig.type === "systolic/diastolic") {
      return value ? `${value.systolic || ""}/${value.diastolic || ""}` : "N/A";
    }
    return value ?? "N/A";
  }

  // Fallback for old structure
  if (vitalConfig.type === "systolic/diastolic") {
    return entry.systolic && entry.diastolic
      ? `${entry.systolic}/${entry.diastolic}`
      : "N/A";
  }
  return entry[vitalConfig.id] ?? "N/A";
};

const DEFAULT_VITALS_CONFIG = [
  {
    id: "bloodPressure",
    name: "Blood Pressure",
    unit: "mmHg",
    enabled: true,
    isDefault: true,
    type: "systolic/diastolic",
  },
  {
    id: "heartRate",
    name: "Heart Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
  },
  {
    id: "temperature",
    name: "Temperature",
    unit: "°C",
    enabled: true,
    isDefault: true,
  },
  {
    id: "respiratoryRate",
    name: "Respiratory Rate",
    unit: "BPM",
    enabled: true,
    isDefault: true,
  },
  {
    id: "o2Sat",
    name: "O2 Saturation",
    unit: "%",
    enabled: true,
    isDefault: true,
  },
  {
    id: "bloodSugar",
    name: "Blood Sugar",
    unit: "mg/dL",
    enabled: true,
    isDefault: true,
  },
];

const iconMap = {
  bloodPressure: <FaHeartbeat className="mx-auto text-red-500 text-2xl mb-1" />,
  heartRate: <GiHealthNormal className="mx-auto text-red-500 text-2xl mb-1" />,
  temperature: (
    <FaThermometerHalf className="mx-auto text-red-500 text-2xl mb-1" />
  ),
  bloodSugar: <FaTint className="mx-auto text-red-500 text-2xl mb-1" />,
  respiratoryRate: <FaWind className="mx-auto text-red-500 text-2xl mb-1" />,
  o2Sat: <GiHealthNormal className="mx-auto text-red-500 text-2xl mb-1" />,
  default: <FaPlusSquare className="mx-auto text-red-500 text-2xl mb-1" />,
};

const DAILY_CARE_LOG_PATH = "/daily-care-log";

const RecentVitals = ({ vitals }) => {
  const { activeCareProfile } = useAuth();
  const [vitalsConfig, setVitalsConfig] = useState(DEFAULT_VITALS_CONFIG);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!activeCareProfile) {
        setVitalsConfig(DEFAULT_VITALS_CONFIG);
        return;
      }
      const profileRef = doc(db, "careProfiles", activeCareProfile.id);
      const docSnap = await getDoc(profileRef);
      if (docSnap.exists() && docSnap.data().vitalsConfig) {
        setVitalsConfig(docSnap.data().vitalsConfig);
      } else {
        setVitalsConfig(DEFAULT_VITALS_CONFIG);
      }
    };
    fetchConfig();
  }, [activeCareProfile]);

  if (!vitals) {
    return (
      <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-3xl border border-slate-200/80 bg-white/95 p-6 text-center shadow-sm">
        <p className="text-sm text-slate-500 mb-4">
          No recent vitals recorded.
        </p>
        <Link
          to={DAILY_CARE_LOG_PATH}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700"
        >
          Add Daily Care Log →
        </Link>
      </div>
    );
  }

  const { dateTime, notes } = vitals;
  const activeVitals = vitalsConfig.filter((v) => v.enabled);

  return (
    <section className="flex h-full min-h-[260px] flex-col rounded-3xl border border-slate-200/80 bg-white/95 p-5 shadow-sm md:p-6">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
          Health
        </p>
        <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-950">
          Latest Vitals
        </h2>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-3 text-center sm:grid-cols-3">
        {activeVitals.map((vital) => {
          const value = getVitalValue(vitals, vital);
          if (value === "N/A" || !value || value === "/") return null; // Don't render if no value

          return (
            <Link
              key={vital.id}
              to={DAILY_CARE_LOG_PATH}
              className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-3 transition hover:bg-white hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {iconMap[vital.id] || iconMap.default}
              <p className="text-lg font-semibold text-slate-950">
                {value}
                {vital.id === "temperature" && "°C"}
                {vital.id === "o2Sat" && "%"}
              </p>
              <p className="text-xs text-slate-500">{vital.name}</p>
            </Link>
          );
        })}
      </div>
      {notes && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-3">
          <h4 className="font-semibold text-sm text-slate-700 flex items-center mb-1">
            <FaNotesMedical className="mr-2 text-slate-400" />
            Notes
          </h4>
          <p className="text-sm text-slate-600">{notes}</p>
        </div>
      )}
      <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-400">
          Recorded on:{" "}
          {dateTime
            ? new Date(dateTime.seconds * 1000).toLocaleString()
            : "N/A"}
        </p>
        <Link
          to={DAILY_CARE_LOG_PATH}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700"
        >
          Open Daily Care Log →
        </Link>
      </div>
    </section>
  );
};

export default RecentVitals;
